import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { Hero } from './components/Hero';
import { HowItWorks } from './components/HowItWorks';
import { PopularServices } from './components/PopularServices';
import { StatsBar } from './components/StatsBar';
import { Testimonials } from './components/Testimonials';
import { CTABanner } from './components/CTABanner';
import { Footer } from './components/Footer';
import { QuoteForm } from './components/QuoteForm';
import { BrandedLandingPage } from './components/BrandedLandingPage';
export function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [view, setView] = useState<'landing' | 'quote-form' | 'branded-landing'>('landing');
  const [searchData, setSearchData] = useState({
    service: '',
    zip: ''
  });
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleSearch = (service: string, zip: string) => {
    setSearchData({
      service,
      zip
    });
    setView('quote-form');
    window.scrollTo(0, 0);
  };
  const handleServiceClick = (serviceSlug: string) => {
    setSearchData({
      service: serviceSlug,
      zip: ''
    });
    setView('quote-form');
    window.scrollTo(0, 0);
  };
  if (view === 'branded-landing') {
    return <BrandedLandingPage onClose={() => setView('landing')} data-id="element-0" />;
  }
  if (view === 'quote-form') {
    return <QuoteForm initialService={searchData.service} initialZip={searchData.zip} onClose={() => setView('landing')} data-id="element-1" />;
  }
  return <div className="min-h-screen bg-white font-sans selection:bg-amber-500 selection:text-white" data-id="element-2">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-md py-4' : 'bg-transparent py-6'}`} data-id="element-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4">
          <div className="flex items-center justify-between" data-id="element-5">
            {/* Logo */}
            <a href="#" className="text-2xl font-extrabold text-white tracking-tight focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-sm" data-id="element-6">
              FindService<span className="text-amber-500" data-id="element-7">Quotes</span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8" data-id="element-8">
              <a href="#services" className="text-sm font-medium text-slate-200 hover:text-white transition-colors" data-id="element-9">
                Services
              </a>
              <a href="#how-it-works" className="text-sm font-medium text-slate-200 hover:text-white transition-colors" data-id="element-10">
                How It Works
              </a>
              <a href="#reviews" className="text-sm font-medium text-slate-200 hover:text-white transition-colors" data-id="element-11">
                Reviews
              </a>
              <button onClick={() => setView('branded-landing')} className="text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors border border-amber-500/30 px-3 py-1.5 rounded-md" data-id="element-12">
                Champion Demo
              </button>
              <button className="h-10 px-5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-slate-900" data-id="element-13">
                Get Free Quotes
              </button>
            </div>

            {/* Mobile Menu Toggle */}
            <button className="md:hidden p-2 text-slate-200 hover:text-white focus:outline-none" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-14">
              {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-15" /> : <MenuIcon className="w-6 h-6" data-id="element-16" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-slate-900 border-t border-slate-800 shadow-xl" data-id="element-17">
            <div className="px-4 pt-2 pb-6 space-y-1 flex flex-col" data-id="element-18">
              <a href="#services" className="block px-3 py-4 text-base font-medium text-slate-200 hover:text-white hover:bg-slate-800 rounded-md" onClick={() => setMobileMenuOpen(false)} data-id="element-19">
                Services
              </a>
              <a href="#how-it-works" className="block px-3 py-4 text-base font-medium text-slate-200 hover:text-white hover:bg-slate-800 rounded-md" onClick={() => setMobileMenuOpen(false)} data-id="element-20">
                How It Works
              </a>
              <a href="#reviews" className="block px-3 py-4 text-base font-medium text-slate-200 hover:text-white hover:bg-slate-800 rounded-md" onClick={() => setMobileMenuOpen(false)} data-id="element-21">
                Reviews
              </a>
              <button onClick={() => {
            setView('branded-landing');
            setMobileMenuOpen(false);
          }} className="block w-full text-left px-3 py-4 text-base font-medium text-amber-400 hover:text-amber-300 hover:bg-slate-800 rounded-md" data-id="element-22">
                Champion Demo
              </button>
              <div className="pt-4 px-3" data-id="element-23">
                <button className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors" data-id="element-24">
                  Get Free Quotes
                </button>
              </div>
            </div>
          </div>}
      </nav>

      {/* Main Content */}
      <main data-id="element-25">
        <Hero onSearch={handleSearch} data-id="element-26" />
        <HowItWorks data-id="element-27" />
        <PopularServices onServiceClick={handleServiceClick} data-id="element-28" />
        <StatsBar data-id="element-29" />
        <Testimonials data-id="element-30" />
        <CTABanner data-id="element-31" />
      </main>

      {/* Footer */}
      <Footer data-id="element-32" />
    </div>;
}